(ns practitest.frontend.et.db
  (:require
    [cljs.spec.alpha :as s]
    [clojure.string :as string]
    [re-frame.core :as re-frame]))

;; ===========================================================================
;; spec
;;
;; This is a clojure.spec specification for the value in app-db. It is like a
;; Schema. See: http://clojure.org/guides/spec
;;
;; The value in app-db should always match this spec. Only event handlers
;; can change the value in app-db so, after each event handler
;; has run, we re-check app-db for correctness (complience with the Schema).
;;
;; How is this done? Look in events.cljs and you'll notice that all handlers
;; have an "after" interceptor which does the spec re-check.
;;
;; None of this is strictly necessary. It could be omitted. But we find it
;; good practice.
(s/def :et.db/db (s/keys :req-un []))

;; initial app-db
(def default-value
  {})

(defn uri-prefix [{:keys [project-id testset-id]}]
  (str "/p/" project-id "/sets/" testset-id))

(defn soil [entity path]
  (update entity :dirty-fields #(set (conj % path))))

(defn update-entity* [entity path value]
  (let [f (if (vector? path) assoc-in assoc)]
    (-> entity (f path value) (soil path))))

(defn update-entity [db path value]
  (update-in db [:et-state :entity] update-entity* path value))

(defn set-field [db [id value]]
  (update-entity db [:fields id] value))

(defn set-charter [db [value]]
  (update-entity db :charter value))

(defn set-guide-points [db [value]]
  (update-entity db :guide-points value))

(defn set-estimate [db [value]]
  (update-entity db :estimate value))

(defn set-run-status [db [value]]
  (update-entity db :run-status value))

(defn update-annotation* [annotation path value]
  (-> annotation (assoc path value) (soil path)))

(defn update-annotation [db position path value]
  (update-in db [:et-state :entity :annotations position] update-annotation* path value))

(defn set-annotation-type [db [position value]]
  (update-annotation db position :type value))

(defn set-annotation-description [db [position value]]
  (update-annotation db position :description value))

;; TODO add schema for entity, should help people who will maintain this code
;;      to understand the structure of the entity.
(defn move-annotation-up*
  "Moves annotation at `position` up.
   If the `position` is 0, will do nothing.
   Otherwise, will swap previous and current annotations,
   adding `:position` to the set of dirty fields for both.
   Returns updated entity."
  [entity position]
  (if (pos? position)
    (update entity :annotations
            (fn [annotations]
              (let [[before after] (split-at position annotations)
                    prev (last before)
                    before (butlast before)
                    curr (first after)
                    after (rest after)]
                (vec
                  (concat (vec before)
                          [(soil curr :position)
                           (soil prev :position)]
                          (vec after))))))
    entity))

(defn move-annotation-up [db [position]]
  (update-in db [:et-state :entity] move-annotation-up* position))

(defn move-annotation-down*
  "Move annotation at `position` down.
   If the `position` is last position, will do nothing.
   Otherwise, will swap current and next annotations,
   adding `:position` to the set of dirty fields for both.
   Returns updated entity."
  [entity position]
  (if (< position (dec (count (:annotations entity))))
    (update entity :annotations
            (fn [annotations]
              (let [[before after] (split-at position annotations)
                    curr (first after)
                    next (second after)
                    after (drop 2 after)]
                (vec
                  (concat (vec before)
                          [(soil next :position)
                           (soil curr :position)]
                          (vec after))))))
    entity))

(defn move-annotation-down [db [position]]
  (update-in db [:et-state :entity] move-annotation-down* position))

(defn remove-annotation*
  "Removes annotation at `position`.
   If the annotation has an ID (was previously saved), we will add it to the
   `:annotations-to-delete` set.
   Updates the positions of all annotations after the current one.
   Returns updated entity."
  [entity position]
  (let [a-id (get-in entity [:annotations position :id])]
    (cond-> entity
            a-id (update :annotations-to-delete #(set (conj % a-id)))
            :always (update :annotations
                            (fn [annotations]
                              (let [focus (if (= (dec (count annotations)) position)
                                            (dec position)
                                            position)
                                    [before after] (split-at position annotations)
                                    after (rest after)]
                                (vec
                                  (map-indexed (fn [i annotation]
                                                 (assoc annotation
                                                   :focus (= focus i)
                                                   :initial-focus (= focus i)))
                                               (concat (vec before)
                                                       (vec (map #(soil % :position) after)))))))))))

(defn remove-annotation [db [position]]
  (update-in db [:et-state :entity] remove-annotation* position))

(defn add-annotation*
  "Adds `annotation` at `position`.
   If `position` is `nil`, will just add the annotation to the end of the list.
   Otherwise, will update the positions of all annotations after the current one.
   Returns updated entity."
  [entity position annotation]
  (update entity :annotations
          (fn [annotations]
            (if position
              (let [[before after] (split-at position (map #(dissoc % :focus :initial-focus) annotations))]
                (vec
                  (concat (vec before)
                          [(soil annotation :position)]
                          (vec (map #(soil % :position) after)))))
              (conj (vec annotations) (soil annotation :position))))))

(defn add-annotation [db [position]]
  (let [annotation {:type                (if (seq (get-in db [:et-state :entity :annotations])) (->> @(re-frame/subscribe [:et/annotation-types])
                                                                                                     second
                                                                                                     :label)
                                                                                                (->> @(re-frame/subscribe [:et/annotation-types])
                                                                                                     first
                                                                                                     :label)) ; "Note"2 "Setup"1
                    :focus               true
                    :initial-focus       true
                    :filter-display-flag true
                    :description         ""
                    :attachments         []
                    :dirty-fields        #{:type :description}
                    :uuid                (random-uuid)}]
    (update-in db [:et-state :entity] add-annotation* position annotation)))

(defn add-annotation-attachment*
  "Adds `attachment` to annotation at `annotation-position`.
   Returns updated entity."
  [entity annotation-position attachment]
  (update-in entity [:annotations annotation-position]
             (fn [annotation]
               (-> annotation
                   (update :attachments #(vec (conj % (assoc attachment :uuid (random-uuid)))))
                   (soil :attachments)))))

(defn add-annotation-attachment [db [annotation-position attachment]]
  (update-in db [:et-state :entity] add-annotation-attachment* annotation-position attachment))

(defn remove-annotation-attachment*
  "Removes attachment at position `attachment-position` from
   annotation at position `annotation-position`.
   Returns updated entity."
  [entity annotation-position attachment-position]
  (update-in entity [:annotations annotation-position]
             (fn [annotation]
               (let [a-id (get-in annotation [:attachments attachment-position :id])]
                 (cond-> annotation
                         a-id (update :attachments-to-delete #(set (conj % a-id)))
                         a-id (soil :attachments)
                         :always (update :attachments
                                         (fn [attachments]
                                           (let [[before after] (split-at attachment-position attachments)]
                                             (vec
                                               (concat (vec before) (vec (rest after))))))))))))

(defn remove-annotation-attachment [db [annotation-position attachment-position]]
  (update-in db [:et-state :entity] remove-annotation-attachment* annotation-position attachment-position))

(defn filter-annotation [db annotation]
  (let [description-filter (get-in db [:et-state :description-filter])
        type-filter (get-in db [:et-state :type-filter])]
    (and
      (or (string/blank? description-filter)
          (string/includes? (string/lower-case (:description annotation)) (string/lower-case description-filter)))
      (or (string/blank? type-filter)
          (= (:type annotation) type-filter)))))

(defn change-annotations-filter-flag [db]
  (update-in db [:et-state :entity :annotations]
             (fn [curr-annotations]
               (vec (map (fn [annotation]
                           (assoc annotation :filter-display-flag
                                             (filter-annotation db annotation)))
                         curr-annotations)))))
